/**
 * HiuConsentRequestPage — /org/health-info/request
 *
 * Step 1 of the M3 HIU flow (HIU_FLOW_101/102): find/verify a patient by ABHA, then
 * submit a consent request for their external health history.
 *
 * Patient lookup reuses ABHAVerificationFlow directly (same standalone-mount pattern
 * ABHAPage.jsx already uses for its "Verify Existing" tab) rather than the heavier
 * ABHASection wrapper, which carries registration-specific baggage this flow doesn't
 * need. patient_name is a local-only field (never sent to ABDM — see hiuApi.js) so the
 * verified profile's name is used as-is.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import AdminPageHeader from '../components/common/AdminPageHeader';
import ABHAVerificationFlow from '../components/abha/ABHAVerificationFlow';
import { useTheme } from '../theme/ThemeContext';
import { requestConsent } from '../services/api/hiuApi';
import { maskAbhaAddress } from '../services/api/abhaProfile';
import { fetchStaffList } from '../services/api/staffApi';
import { useAuth } from '../context/AuthContext';
import { STAFF_ROLES } from '../constants/staff';
import { formatDoctorName } from '../utils/staffName';
import { ROUTES } from '../constants/routes';
import { HI_TYPES } from '../constants/abdmHiTypes';
import { getTodayIST, istDateToISOString } from '../utils/dateIST';
import { OTHER_ORGS, ABDM_ICONS } from '../constants/abdmCopy';
import { hiTypeLabel } from '../constants/abdmHiTypes';
import { PURPOSE_CODES } from '../constants/abdmPurposes';
import AbdmEnabledGate from '../components/common/AbdmEnabledGate';

// Live-caught 2026-09-12: ABDM rejected a submission ("Date must be a present/before
// date") because the "to" field defaulted to tomorrow. The old todayISO() used
// `new Date().toISOString().slice(0, 10)`, which converts to UTC — wrong whenever the
// browser's local/system timezone isn't actually IST (as here) or, even on a correctly
// configured IST machine, between midnight and 05:30 IST when the UTC date still points
// at the previous day. getTodayIST() is this codebase's canonical fix for exactly that
// (see its own doc comment) — anchored to Asia/Kolkata regardless of the OS timezone,
// since the backend stamps all ABDM dates in IST. Reuse it instead of a local ad-hoc
// UTC-based helper.
function addMonthsIST(months) {
  const ist = new Date(Date.now() + 5.5 * 60 * 60 * 1000);
  ist.setUTCMonth(ist.getUTCMonth() + months);
  const y = ist.getUTCFullYear();
  const m = String(ist.getUTCMonth() + 1).padStart(2, '0');
  const d = String(ist.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// FT-R6 "Request expires in": a preset (minutes) or 'custom' (number + unit). We send the DURATION in
// whole minutes (the server adds it to its own clock, so a wrong PC clock can't matter); allowed
// 2..1440, same as the server. The default is the longest preset.
const EXPIRY_MIN_MINUTES = 2;
const EXPIRY_MAX_MINUTES = 24 * 60;
const EXPIRY_DEFAULT_MINUTES = EXPIRY_MAX_MINUTES;
const EXPIRY_CUSTOM = 'custom';

// Chosen duration in whole minutes (custom hours may be decimal: 1.5 h = 90; rounded to the nearest
// minute), or null when a custom entry is empty / not a number / outside 2 min - 24 h.
function expiryMinutes(choice, amount, unit) {
  if (choice !== EXPIRY_CUSTOM) return choice;
  const minutes = Number(amount) * (unit === 'hours' ? 60 : 1);
  return Number.isFinite(minutes) && minutes >= EXPIRY_MIN_MINUTES && minutes <= EXPIRY_MAX_MINUTES
    ? Math.round(minutes)
    : null;
}

const SERVER_FIELD_TO_FORM = {
  date_from: 'dateFrom', date_to: 'dateTo', data_erase_at: 'dataEraseAt',
  hi_types: 'hiTypes', purpose_code: 'purposeCode', request_expires_in_minutes: 'requestExpiry',
};
// R71 codes: requester_member_id 422s land on the Requesting doctor field.
const REQUESTER_ERROR_CODES = ['REQUESTER_DOCTOR_REQUIRED', 'REQUESTER_INVALID'];

const inputStyle = (isDark) => ({
  width: '100%',
  padding: '8px 10px',
  borderRadius: 8,
  fontSize: 14,
  background: isDark ? '#0f172a' : '#ffffff',
  color: isDark ? '#f1f5f9' : '#111827',
  border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#d1d5db'}`,
});

// Toggle chip (record types, request expiry): click-first, wraps on narrow screens.
const chipStyle = (isDark, active) => ({
  padding: '6px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600,
  cursor: 'pointer',
  background: active ? '#0369a1' : (isDark ? '#1e293b' : '#f1f5f9'),
  color: active ? '#ffffff' : (isDark ? '#e2e8f0' : '#374151'),
  border: `1px solid ${active ? '#0369a1' : (isDark ? 'rgba(255,255,255,0.15)' : '#d1d5db')}`,
});

const labelStyle = (isDark) => ({
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  marginBottom: 4,
  color: isDark ? '#e2e8f0' : '#374151',
});

function HiuConsentRequestPageBody() {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  // R71: a doctor requests as themselves; everyone else must choose the requesting doctor.
  const { user } = useAuth();
  const isDoctor = String(user?.role || '').toUpperCase() === STAFF_ROLES.DOCTOR;

  const [profile, setProfile] = useState(null); // { name, abhaAddress, abhaNumber, ... }
  const [form, setForm] = useState({
    purposeCode: 'CAREMGT',
    // Default to ALL types, per ABDM's own FAQ (Q47, Milestone 3): a narrowly-scoped
    // request whose HI types don't match what the patient's linked care contexts are
    // actually tagged with fails at grant time in the PHR app with "There is no
    // facility available to share the health records within given information
    // request duration" — a confusing, ABDM-side-looking error that's actually just
    // an under-scoped request. Still fully deselectable via toggleHiType below if a
    // narrower request is genuinely wanted.
    // OR-119 (owner 2026-11-03): Invoice pre-ticked too; the sandbox accepted a received Invoice (reverses OR-29).
    hiTypes: [...HI_TYPES],
    dateFrom: '',
    dateTo: getTodayIST(),
    dataEraseAt: addMonthsIST(12),
  });
  // Request expiry lives outside `form`: it is a duration (minutes) and is sent as one.
  const [expiryChoice, setExpiryChoice] = useState(EXPIRY_DEFAULT_MINUTES);
  const [customAmount, setCustomAmount] = useState('');
  const [customUnit, setCustomUnit] = useState('minutes');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Requesting-doctor picker (non-doctor callers only). The server fills the requester's
  // name + registration number from the chosen doctor's staff record.
  const [doctors, setDoctors] = useState([]);
  const [doctorsLoading, setDoctorsLoading] = useState(false);
  const [doctorsError, setDoctorsError] = useState('');
  const [selectedDoctorId, setSelectedDoctorId] = useState('');

  const handleVerified = (raw) => {
    setProfile({ name: raw.name, abhaAddress: raw.abhaAddress, abhaNumber: raw.abhaNumber });
  };

  useEffect(() => {
    if (!profile || isDoctor) return;
    let cancelled = false;
    setDoctorsLoading(true);
    setDoctorsError('');
    fetchStaffList()
      .then((res) => {
        if (cancelled) return;
        if (res?.success) {
          const active = (res.data || [])
            .filter((m) => (m.role || '').toUpperCase() === STAFF_ROLES.DOCTOR)
            .filter((m) => (m.status || 'ACTIVE').toUpperCase() === 'ACTIVE');
          setDoctors(active);
        } else {
          setDoctorsError(res?.error || 'Failed to load doctor list.');
        }
      })
      .finally(() => { if (!cancelled) setDoctorsLoading(false); });
    return () => { cancelled = true; };
  }, [profile, isDoctor]);

  const toggleHiType = (type) => {
    setForm((f) => ({
      ...f,
      hiTypes: f.hiTypes.includes(type)
        ? f.hiTypes.filter((t) => t !== type)
        : [...f.hiTypes, type],
    }));
  };

  const validate = () => {
    const next = {};
    if (form.hiTypes.length === 0) next.hiTypes = OTHER_ORGS.pickType;
    if (!form.dateFrom) next.dateFrom = 'Required.';
    if (!form.dateTo) next.dateTo = 'Required.';
    // Same rules/messages as the backend validator (consent/request_validation.py).
    const today = getTodayIST();
    if (form.dateFrom && form.dateTo && form.dateFrom > form.dateTo) {
      next.dateFrom = 'Start date must be on or before the end date.';
    }
    if (form.dateTo && form.dateTo > today) next.dateTo = "End date can't be in the future.";
    if (!form.dataEraseAt) next.dataEraseAt = 'Required.';
    else if (form.dataEraseAt <= today) next.dataEraseAt = 'Access-until date must be in the future.';
    if (expiryMinutes(expiryChoice, customAmount, customUnit) === null) next.requestExpiry = OTHER_ORGS.expiryCustomError;
    if (!form.purposeCode) next.purposeCode = OTHER_ORGS.purposeMissing;
    if (!isDoctor && !selectedDoctorId) next.requesterMemberId = 'Choose the requesting doctor.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Live-caught 2026-09-12: `profile` can be a truthy object with an empty abhaAddress
    // (an ABDM sandbox response gap in the aadhaar/abha-number "get_profile experiment"
    // path — see app/abdm/domain/verification/service.py) — checking !profile alone let
    // a request through with patient.id="" straight to ABDM. Require the address itself.
    if (!profile || !profile.abhaAddress) {
      setSubmitError(OTHER_ORGS.verifyFirst);
      return;
    }
    if (!validate()) return;

    setSubmitting(true);
    setSubmitError('');
    try {
      await requestConsent({
        patientAbhaAddress: profile.abhaAddress,
        patientName: profile.name,
        purposeCode: form.purposeCode,
        hiTypes: form.hiTypes,
        dateFrom: istDateToISOString(form.dateFrom),
        // N3: for today, midnight IST is in the past but "end of today" is not;
        // send the current instant so the range covers today's records.
        dateTo: form.dateTo === getTodayIST() ? new Date().toISOString() : istDateToISOString(form.dateTo),
        dataEraseAt: istDateToISOString(form.dataEraseAt),
        // FT-R6: the chosen span in minutes; the server turns it into an instant on its own clock.
        requestExpiresInMinutes: expiryMinutes(expiryChoice, customAmount, customUnit),
        requesterMemberId: isDoctor ? undefined : selectedDoctorId,
      });
      navigate(ROUTES.HIU_CONSENTS);
    } catch (err) {
      const slot = err.code === 'CONSENT_REQUEST_INVALID' && SERVER_FIELD_TO_FORM[err.field];
      if (REQUESTER_ERROR_CODES.includes(err.code)) {
        // The requester field only renders for non-doctors; doctors get the page banner.
        if (isDoctor) {
          setSubmitError(err.userMessage || 'Your registration number is missing from your staff profile. Ask your admin to add it before requesting records.');
        } else {
          setErrors({ requesterMemberId: err.userMessage || err.message });
        }
        return;
      }
      if (slot) {
        // Server 422: show on the field, stay on the page.
        setErrors({ [slot]: err.userMessage || err.message });
        return;
      }
      setSubmitError(err.userMessage || OTHER_ORGS.sendError);
    } finally {
      setSubmitting(false);
    }
  };

  const bg = isDark
    ? 'linear-gradient(135deg, #0E0E0F 0%, #1a1a2e 50%, #0E0E0F 100%)'
    : 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 50%, #f8fafc 100%)';

  return (
    <div style={{ minHeight: '100vh', background: bg }}>
      <AdminPageHeader />

      <main style={{ maxWidth: 640, margin: '0 auto', padding: '24px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: isDark ? '#f5f5f7' : '#111827', margin: 0 }}>
            {ABDM_ICONS.OTHER_ORGS} {OTHER_ORGS.requestTitle}
          </h1>
          <button
            onClick={() => navigate(ROUTES.HIU_CONSENTS)}
            style={{
              background: isDark ? '#1e293b' : '#f1f5f9', color: isDark ? '#f1f5f9' : '#374151',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#d1d5db'}`,
              borderRadius: 8, padding: '10px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
            }}
          >
            {OTHER_ORGS.requestBack}
          </button>
        </div>
        <p style={{ fontSize: 14, color: isDark ? '#9ca3af' : '#6b7280', marginTop: 4, marginBottom: 24 }}>
          {OTHER_ORGS.requestSubtitle}
        </p>

        <div style={{
          background: isDark ? 'rgba(255,255,255,0.03)' : '#ffffff',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e5e7eb'}`,
          borderRadius: 12,
          padding: 20,
        }}>
          {!profile ? (
            <ABHAVerificationFlow
              onComplete={handleVerified}
              onSkip={() => navigate(ROUTES.ORG)}
              title={OTHER_ORGS.findTitle}
              subtitle={OTHER_ORGS.findSubtitle}
            />
          ) : (
            <form onSubmit={handleSubmit}>
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: isDark ? 'rgba(5,150,105,0.12)' : '#f0fdf4',
                border: `1px solid ${isDark ? '#065f46' : '#bbf7d0'}`,
                borderRadius: 8, padding: '10px 14px', marginBottom: 20,
              }}>
                <div>
                  <p style={{ margin: 0, fontWeight: 600, color: isDark ? '#f1f5f9' : '#111827' }}>
                    {profile.name || 'Patient'}
                  </p>
                  <p style={{ margin: 0, fontSize: 12, color: isDark ? '#94a3b8' : '#6b7280' }}>
                    {maskAbhaAddress(profile.abhaAddress)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setProfile(null)}
                  style={{
                    background: isDark ? '#1e293b' : '#f1f5f9',
                    color: isDark ? '#f1f5f9' : '#374151',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#d1d5db'}`,
                    borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer',
                  }}
                >
                  Change
                </button>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle(isDark)}>Purpose</label>
                <select
                  value={form.purposeCode}
                  onChange={(e) => setForm((f) => ({ ...f, purposeCode: e.target.value }))}
                  style={inputStyle(isDark)}
                >
                  {PURPOSE_CODES.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
                {errors.purposeCode && (
                  <p style={{ color: isDark ? '#f87171' : '#dc2626', fontSize: 12, marginTop: 4 }}>{errors.purposeCode}</p>
                )}
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle(isDark)}>{OTHER_ORGS.recordTypes}</label>
                <p style={{ fontSize: 12, color: isDark ? '#9ca3af' : '#6b7280', margin: '0 0 8px' }}>
                  {OTHER_ORGS.recordTypesHint}
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {HI_TYPES.map((type) => {
                    const active = form.hiTypes.includes(type);
                    return (
                      <button
                        type="button"
                        key={type}
                        onClick={() => toggleHiType(type)}
                        style={chipStyle(isDark, active)}
                      >
                        {hiTypeLabel(type)}
                      </button>
                    );
                  })}
                </div>
                {errors.hiTypes && (
                  <p style={{ color: isDark ? '#f87171' : '#dc2626', fontSize: 12, marginTop: 6 }}>{errors.hiTypes}</p>
                )}
              </div>

              <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle(isDark)}>From date</label>
                  <input
                    type="date" value={form.dateFrom}
                    onChange={(e) => setForm((f) => ({ ...f, dateFrom: e.target.value }))}
                    style={inputStyle(isDark)}
                  />
                  {errors.dateFrom && (
                    <p style={{ color: isDark ? '#f87171' : '#dc2626', fontSize: 12, marginTop: 4 }}>{errors.dateFrom}</p>
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle(isDark)}>To date</label>
                  <input
                    type="date" value={form.dateTo}
                    onChange={(e) => setForm((f) => ({ ...f, dateTo: e.target.value }))}
                    style={inputStyle(isDark)}
                  />
                  {errors.dateTo && (
                    <p style={{ color: isDark ? '#f87171' : '#dc2626', fontSize: 12, marginTop: 4 }}>{errors.dateTo}</p>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle(isDark)}>{OTHER_ORGS.accessUntil}</label>
                <input
                  type="date" value={form.dataEraseAt}
                  onChange={(e) => setForm((f) => ({ ...f, dataEraseAt: e.target.value }))}
                  style={inputStyle(isDark)}
                />
                {errors.dataEraseAt && (
                  <p style={{ color: isDark ? '#f87171' : '#dc2626', fontSize: 12, marginTop: 4 }}>{errors.dataEraseAt}</p>
                )}
              </div>

              <div style={{ marginBottom: 16 }}>
                <div style={labelStyle(isDark)} id="request-expiry-label">{OTHER_ORGS.requestExpiresIn}</div>
                <div role="group" aria-labelledby="request-expiry-label" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {[
                    ...OTHER_ORGS.expiryPresets.map((p) => ({ value: p.minutes, label: p.label })),
                    { value: EXPIRY_CUSTOM, label: OTHER_ORGS.expiryCustom },
                  ].map((opt) => (
                    <button
                      type="button" key={opt.value} aria-pressed={expiryChoice === opt.value}
                      onClick={() => setExpiryChoice(opt.value)}
                      style={chipStyle(isDark, expiryChoice === opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {expiryChoice === EXPIRY_CUSTOM && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 8 }}>
                    <input
                      type="number" inputMode="decimal" step="any" value={customAmount}
                      aria-label={OTHER_ORGS.expiryCustomAmount}
                      onChange={(e) => setCustomAmount(e.target.value)}
                      style={{ ...inputStyle(isDark), width: 110 }}
                    />
                    {Object.entries(OTHER_ORGS.expiryUnits).map(([unit, label]) => (
                      <button
                        type="button" key={unit} aria-pressed={customUnit === unit}
                        onClick={() => setCustomUnit(unit)}
                        style={chipStyle(isDark, customUnit === unit)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                )}
                <p style={{ fontSize: 12, color: isDark ? '#9ca3af' : '#6b7280', margin: '6px 0 0' }}>
                  {OTHER_ORGS.requestExpiresHelp}
                </p>
                {errors.requestExpiry && (
                  <p style={{ color: isDark ? '#f87171' : '#dc2626', fontSize: 12, marginTop: 4 }}>{errors.requestExpiry}</p>
                )}
              </div>

              {!isDoctor && (
                <div style={{ marginBottom: 20 }}>
                  <label style={labelStyle(isDark)}>Requesting doctor</label>
                  {doctorsLoading ? (
                    <p style={{ fontSize: 13, color: isDark ? '#9ca3af' : '#6b7280', margin: '8px 0' }}>Loading doctors…</p>
                  ) : doctorsError ? (
                    <p style={{ fontSize: 12, color: isDark ? '#f87171' : '#dc2626', margin: '8px 0' }}>{doctorsError}</p>
                  ) : (
                    <select
                      value={selectedDoctorId}
                      onChange={(e) => setSelectedDoctorId(e.target.value)}
                      style={inputStyle(isDark)}
                    >
                      <option value="">— choose doctor —</option>
                      {doctors.map((m) => (
                        <option key={m.member_id} value={m.member_id}>{formatDoctorName(m)}</option>
                      ))}
                    </select>
                  )}
                  {errors.requesterMemberId && (
                    <p style={{ color: isDark ? '#f87171' : '#dc2626', fontSize: 12, marginTop: 4 }}>{errors.requesterMemberId}</p>
                  )}
                </div>
              )}

              {submitError && (
                <p style={{ color: isDark ? '#f87171' : '#dc2626', fontSize: 13, marginBottom: 12 }}>{submitError}</p>
              )}

              <button
                type="submit"
                disabled={submitting}
                style={{
                  width: '100%', padding: '10px 16px', borderRadius: 8, fontSize: 14, fontWeight: 600,
                  background: '#0369a1', color: '#ffffff', border: 'none',
                  opacity: submitting ? 0.6 : 1,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                }}
              >
                {submitting ? OTHER_ORGS.sending : OTHER_ORGS.send}
              </button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}

export default function HiuConsentRequestPage() {
  return (
    <AbdmEnabledGate>
      <HiuConsentRequestPageBody />
    </AbdmEnabledGate>
  );
}
